import { FrameConfig } from '@renderer/types/configState'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { imageUtils } from './imageUtils'
import { clipPlaceholderAperture } from '@frameShapes'
import type { RawSlotMeta } from '@frameShapes'

export interface PhotoState {
  scale: number
  offsetX: number
  offsetY: number
  isMirrored?: boolean
  width: number
  height: number
  naturalWidth: number
  naturalHeight: number
}

/** Cover scale to fill slot (same idea as PrintPreview initial photo scale). */
function coverScaleInSlot(assetW: number, assetH: number, slotW: number, slotH: number): number {
  if (assetW <= 0 || assetH <= 0 || slotW <= 0 || slotH <= 0) return 1
  return Math.max(slotW / assetW, slotH / assetH)
}

/**
 * Map PrintPreview photoState + slot video to a PhotoState for handleVideoDraw so the clip is
 * filled (cover) while preserving the user's zoom relative to default photo cover — avoids
 * letterboxing when video resolution/aspect differs from the still.
 */
function buildVideoPhotoStateForPlaceholder(
  video: HTMLVideoElement,
  slotW: number,
  slotH: number,
  photoState?: PhotoState
): PhotoState {
  const vw = video.videoWidth
  const vh = video.videoHeight
  const baseVideoCover = coverScaleInSlot(vw, vh, slotW, slotH)

  if (!photoState) {
    return {
      scale: baseVideoCover,
      offsetX: 0,
      offsetY: 0,
      width: slotW,
      height: slotH,
      naturalWidth: vw,
      naturalHeight: vh,
      isMirrored: false
    }
  }

  const photoCover = coverScaleInSlot(photoState.naturalWidth, photoState.naturalHeight, slotW, slotH)
  // Never go below 1× default photo cover so video always fills the aperture like object-cover
  const relative = photoCover > 0 ? Math.max(photoState.scale / photoCover, 1) : 1

  return {
    ...photoState,
    scale: baseVideoCover * relative,
    naturalWidth: vw,
    naturalHeight: vh,
    width: slotW,
    height: slotH
  }
}

// Helper function to draw a center-cropped image
function drawCenterCropped(
  ctx: CanvasRenderingContext2D,
  asset: HTMLImageElement | HTMLVideoElement,
  targetX: number,
  targetY: number,
  targetWidth: number,
  targetHeight: number,
  photoState?: PhotoState,
  rotation?: number,
  slotMeta?: RawSlotMeta
): void {
  let effective = photoState
  if (!effective) {
    const srcW =
      asset instanceof HTMLVideoElement ? asset.videoWidth : asset.naturalWidth || asset.width
    const srcH =
      asset instanceof HTMLVideoElement ? asset.videoHeight : asset.naturalHeight || asset.height
    if (srcW > 0 && srcH > 0) {
      const cover = coverScaleInSlot(srcW, srcH, targetWidth, targetHeight)
      effective = {
        scale: cover,
        offsetX: 0,
        offsetY: 0,
        width: targetWidth,
        height: targetHeight,
        naturalWidth: srcW,
        naturalHeight: srcH,
        isMirrored: false
      }
    }
  }

  if (!effective) {
    handleDefaultCrop(ctx, asset, targetX, targetY, targetWidth, targetHeight)
    return
  }

  if (asset instanceof HTMLVideoElement) {
    handleVideoDraw(ctx, asset, targetX, targetY, targetWidth, targetHeight, effective, rotation, slotMeta)
  } else {
    handlePhotoDraw(ctx, asset, targetX, targetY, targetWidth, targetHeight, effective, rotation, slotMeta)
  }
}

/** `image` / `qr` drawParams layers with optional rotation — mirrors renderFrame (pivot at slot center). */
function drawStaticLayerInFrame(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  left: number,
  top: number,
  width: number,
  height: number,
  rotation?: number
): void {
  ctx.save()
  if (rotation) {
    const centerX = left + width / 2
    const centerY = top + height / 2
    ctx.translate(centerX, centerY)
    ctx.rotate(rotation)
    ctx.translate(-centerX, -centerY)
  }
  ctx.drawImage(source, left, top, width, height)
  ctx.restore()
}

function handlePhotoDraw(
  ctx: CanvasRenderingContext2D,
  photo: HTMLImageElement,
  targetX: number,
  targetY: number,
  targetWidth: number,
  targetHeight: number,
  photoState: PhotoState,
  rotation?: number,
  slotMeta?: RawSlotMeta
): void {
  ctx.save()

  const centerX = targetX + targetWidth / 2
  const centerY = targetY + targetHeight / 2

  // Move to center
  ctx.translate(centerX, centerY)

  // Apply rotation from drawParams if specified (for frame layout rotation)
  if (rotation) {
    ctx.rotate(rotation)
  }

  clipPlaceholderAperture(ctx, targetWidth, targetHeight, slotMeta)

  // Apply rotation-aware mirroring if needed (after aperture clip — flips photo only)
  if (photoState.isMirrored) {
    // Get rotation from frame layout (drawParams)
    const frameRotation = rotation ? (rotation * 180) / Math.PI : 0
    // Apply rotation-aware mirroring
    const normalizedRotation = ((frameRotation % 360) + 360) % 360
    switch (normalizedRotation) {
      case 0:
      case 180:
        ctx.scale(-1, 1) // Horizontal flip
        break
      case 90:
      case 270:
        ctx.scale(1, -1) // Vertical flip
        break
    }
  }

  // Apply scale relative to the center
  ctx.scale(photoState.scale, photoState.scale)

  // Apply offsets relative to the scaled dimensions
  ctx.translate(photoState.offsetX / photoState.scale, photoState.offsetY / photoState.scale)

  // Draw centered - photo is already rotated at source level
  ctx.drawImage(photo, -photo.width / 2, -photo.height / 2, photo.width, photo.height)

  ctx.restore()
}

function handleVideoDraw(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  targetX: number,
  targetY: number,
  targetWidth: number,
  targetHeight: number,
  photoState: PhotoState,
  rotation?: number,
  slotMeta?: RawSlotMeta
): void {
  ctx.save()

  const centerX = targetX + targetWidth / 2
  const centerY = targetY + targetHeight / 2

  const vw = Math.max(1, video.videoWidth)
  const vh = Math.max(1, video.videoHeight)
  // Uniform scale that guarantees the video covers the slot (object-cover), after clip
  const minCoverScale = Math.max(targetWidth / vw, targetHeight / vh)
  const videoToPhotoRatio =
    video.videoWidth > 0 ? photoState.naturalWidth / video.videoWidth : 1
  const userScale = photoState.scale * videoToPhotoRatio
  const adjustedScale = Math.max(userScale, minCoverScale)

  // Move to center
  ctx.translate(centerX, centerY)

  // Apply rotation from drawParams if specified (for frame layout rotation)
  if (rotation) {
    ctx.rotate(rotation)
  }

  clipPlaceholderAperture(ctx, targetWidth, targetHeight, slotMeta)

  // Apply rotation-aware mirroring if needed
  if (photoState.isMirrored) {
    // Get rotation from frame layout (drawParams)
    const frameRotation = rotation ? (rotation * 180) / Math.PI : 0
    // Apply rotation-aware mirroring
    const normalizedRotation = ((frameRotation % 360) + 360) % 360
    switch (normalizedRotation) {
      case 0:
      case 180:
        ctx.scale(-1, 1) // Horizontal flip
        break
      case 90:
      case 270:
        ctx.scale(1, -1) // Vertical flip
        break
    }
  }

  ctx.scale(adjustedScale, adjustedScale)

  // Apply offsets adjusted for the adjustedScale relative to the scaled dimensions
  ctx.translate(photoState.offsetX / adjustedScale, photoState.offsetY / adjustedScale)

  // Draw centered - video content maintains original orientation
  ctx.drawImage(video, -vw / 2, -vh / 2, vw, vh)

  ctx.restore()
}

function handleDefaultCrop(
  ctx: CanvasRenderingContext2D,
  asset: HTMLImageElement | HTMLVideoElement,
  targetX: number,
  targetY: number,
  targetWidth: number,
  targetHeight: number
): void {
  ctx.save()

  const sourceWidth = asset instanceof HTMLVideoElement ? asset.videoWidth : asset.width
  const sourceHeight = asset instanceof HTMLVideoElement ? asset.videoHeight : asset.height

  const scaleX = targetWidth / sourceWidth
  const scaleY = targetHeight / sourceHeight
  const scale = Math.max(scaleX, scaleY)

  const scaledWidth = sourceWidth * scale
  const scaledHeight = sourceHeight * scale
  const x = targetX - (scaledWidth - targetWidth) / 2
  const y = targetY - (scaledHeight - targetHeight) / 2

  ctx.drawImage(asset, x, y, scaledWidth, scaledHeight)
  ctx.restore()
}

/** Still hold before each story slot’s clip — shorter than full capture countdown for a snappier strip */
const STORY_INTRO_HOLD_MS = 420
/** Optional tail encoder steps after each story slot (0 = no extra duplicate frames between clips) */
const STORY_SEGMENT_END_EXTRA_STEPS = 0

export async function generateFramedVideo(
  resources: { videoUrl: string; photoUrl: string }[],
  frameConfig: FrameConfig,
  scaleFactor: number = 1,
  id: string,
  recordingSpeed: number,
  FPS: number = 30,
  photoStates?: Record<string, PhotoState>,
  targetDuration: number = 1000
): Promise<File> {
  const { drawParams } = frameConfig
  // Calculate initial dimensions based on scaleFactor
  const initialWidth = Math.round(drawParams.canvasWidth * scaleFactor)
  const initialHeight = Math.round(drawParams.canvasHeight * scaleFactor)

  // Validate dimensions (may resize if exceeds codec limits)
  const validatedDimensions = validateDimensions(initialWidth, initialHeight)

  // Create canvas with validated dimensions
  const canvas = document.createElement('canvas')
  canvas.width = validatedDimensions.width
  canvas.height = validatedDimensions.height
  const ctx = canvas.getContext('2d')!

  // Calculate combined scale factor (scaleFactor * validation scale)
  // This ensures proper rendering when dimensions are resized
  const combinedScaleFactor = scaleFactor * validatedDimensions.scaleFactor

  const canvasRecorder = new Recorder(ctx as RenderingContext, {
    name: 'dreambooth',
    encoderOptions: {
      codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
      width: validatedDimensions.width,
      height: validatedDimensions.height,
      bitrate: 2000000
    },
    duration: Infinity,
    download: false,
    frameRate: FPS
  })

  // Preload all videos; allow empty/invalid videoUrl (static photo fallback for that slot)
  const loadedVideos: (HTMLVideoElement | null)[] = []
  await Promise.all(
    resources.map(async (resource) => {
      const hasValidVideoUrl =
        resource.videoUrl &&
        resource.videoUrl.trim() !== '' &&
        (resource.videoUrl.startsWith('blob:') || resource.videoUrl.startsWith('http'))

      if (!hasValidVideoUrl) {
        loadedVideos.push(null)
        return
      }

      const video = document.createElement('video')
      video.muted = true
      video.autoplay = false
      video.loop = false
      video.src = resource.videoUrl
      video.preload = 'metadata'

      try {
        await Promise.all([
          new Promise<void>((resolve, reject) => {
            video.onloadedmetadata = (): void => resolve()
            video.onerror = (): void => {
              console.warn(`Failed to load video metadata: ${resource.videoUrl}`)
              reject(new Error(`Failed to load video metadata: ${resource.videoUrl}`))
            }
          }),
          new Promise<void>((resolve, reject) => {
            video.onloadeddata = (): void => resolve()
            video.onerror = (): void => {
              console.warn(`Failed to load video: ${resource.videoUrl}`)
              reject(new Error(`Failed to load video: ${resource.videoUrl}`))
            }
          })
        ])
        loadedVideos.push(video)
      } catch {
        loadedVideos.push(null)
      }
    })
  )

  if (process.env.NODE_ENV === 'development') {
    const photoSlots = drawParams.images
      .filter((i) => i.type.startsWith('photo-'))
      .map((i) => parseInt(i.type.split('-')[1], 10))
      .filter((n) => !Number.isNaN(n))
    const expected = resources.length
    if (photoSlots.length > 0 && expected > 0) {
      const sorted = [...new Set(photoSlots)].sort((a, b) => a - b)
      for (let k = 1; k <= expected; k++) {
        if (!sorted.includes(k)) {
          console.warn('[generateFramedVideo] Story: photo slot index gap vs resources', {
            expectedResourceSlots: expected,
            photoSlotsInFrame: sorted,
            missingPhotoIndex: k
          })
          break
        }
      }
      const maxSlot = Math.max(...photoSlots)
      if (maxSlot < expected) {
        console.warn('[generateFramedVideo] Story: max photo-N in frame < resource count', {
          maxSlot,
          expected
        })
      }
    }
  }

  canvasRecorder.start()

  const stepInterval = 1000 / (FPS / recordingSpeed)

  // Apply combined scale transformation to match original frame dimensions
  // This ensures content is rendered at the correct size relative to the validated canvas
  ctx.scale(combinedScaleFactor, combinedScaleFactor)

  // Preload all assets
  const loadedAssets: Record<string, HTMLImageElement> = {}

  // Load QR code as ImageBitmap
  const qrImage = await imageUtils.generateQRCode(id)

  // Preload all photos
  for (let i = 0; i < resources.length; i++) {
    const photoUrl = resources[i].photoUrl
    if (photoUrl) {
      const img = new Image()
      img.src = photoUrl
      await new Promise<void>((resolve) => {
        img.onload = (): void => resolve()
        img.onerror = (): void => {
          console.error(`Failed to load photo: ${photoUrl}`)
          resolve()
        }
      })
      loadedAssets[`photo-${i + 1}`] = img
    }
  }

  // Preload other static images
  await Promise.all(
    drawParams.images
      .filter((img) => img.type === 'image')
      .map(async (imageParams): Promise<void> => {
        const imgSrc = imageParams.src || ''
        if (!imgSrc) return

        try {
          const response = await fetch(imgSrc, {
            method: 'GET',
            mode: 'cors'
          })
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const blob = await response.blob()
          const img = new Image()
          img.src = URL.createObjectURL(blob)
          await new Promise<void>((resolve) => {
            img.onload = (): void => resolve()
            img.onerror = (): void => {
              console.error(`Failed to load image: ${imgSrc}`)
              resolve()
            }
          })
          loadedAssets[imgSrc] = img
        } catch (error) {
          console.error('Error loading image:', error)
        }
      })
  )

  const introHoldMs = Math.min(STORY_INTRO_HOLD_MS, targetDuration)
  const introFrames = Math.max(1, Math.ceil((introHoldMs * FPS) / 1000))

  for (let i = 0; i < resources.length; i++) {
    const resourceIndex = i + 1

    let frameCount = 0
    const startTime = performance.now()

    while (frameCount < introFrames) {
      const currentTime = performance.now()
      const elapsedTime = currentTime - startTime
      const targetFrameTime = (frameCount * 1000) / FPS

      if (elapsedTime >= targetFrameTime) {
        await renderFrame(
          ctx,
          drawParams,
          loadedAssets,
          qrImage,
          resourceIndex,
          true, // Show photos
          null, // No video element yet
          canvasRecorder,
          resources.length,
          photoStates
        )
        frameCount++
      }

      await new Promise((resolve) => setTimeout(resolve, 1))
    }

    // Use preloaded video or static photo fallback when video is missing
    const videoElement = loadedVideos[i]
    if (videoElement) {
      await processVideo(
        ctx,
        videoElement,
        drawParams,
        loadedAssets,
        qrImage,
        resourceIndex,
        canvasRecorder,
        stepInterval,
        resources.length,
        photoStates
      )
    } else {
      // Static photo fallback: match short intro hold (not full capture countdown)
      let fallbackFrameCount = 0
      const fallbackStartTime = performance.now()
      while (fallbackFrameCount < introFrames) {
        const currentTime = performance.now()
        const elapsedTime = currentTime - fallbackStartTime
        const targetFrameTime = (fallbackFrameCount * 1000) / FPS
        if (elapsedTime >= targetFrameTime) {
          await renderFrame(
            ctx,
            drawParams,
            loadedAssets,
            qrImage,
            resourceIndex,
            true,
            null,
            canvasRecorder,
            resources.length,
            photoStates
          )
          fallbackFrameCount++
        }
        await new Promise((resolve) => setTimeout(resolve, 1))
      }
    }

    await renderFrame(
      ctx,
      drawParams,
      loadedAssets,
      qrImage,
      resourceIndex,
      true, // Show photos
      null, // No video element
      canvasRecorder,
      resources.length,
      photoStates
    )

    for (let j = 0; j < STORY_SEGMENT_END_EXTRA_STEPS; j++) {
      await canvasRecorder.step()
    }
  }

  const recordedChunks = await canvasRecorder.stop()

  const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })

  return new File([mp4Blob], `dreambooth-${id}-recording.mp4`, { type: 'video/mp4' })
}

// Modify the existing renderFrame function to support both modes
async function renderFrame(
  ctx: CanvasRenderingContext2D,
  drawParams: FrameConfig['drawParams'],
  loadedAssets: Record<string, HTMLImageElement | ImageBitmap>,
  qrImage: ImageBitmap,
  currentVideoIndex: number,
  showPhotos: boolean,
  videoElement: HTMLVideoElement | null,
  canvasRecorder: Recorder,
  totalResources: number,
  photoStates?: Record<string, PhotoState>
): Promise<void> {
  // Calculate unscaled dimensions
  const transform = ctx.getTransform()
  const unscaledWidth = ctx.canvas.width / transform.a
  const unscaledHeight = ctx.canvas.height / transform.d

  // Draw background
  ctx.fillStyle = drawParams.backgroundColor || '#FFFFFF'
  ctx.fillRect(0, 0, unscaledWidth, unscaledHeight)

  // Draw all elements in their specified order
  for (const imageParams of drawParams.images) {
    if (imageParams.type === 'image' && loadedAssets[imageParams.src || '']) {
      // Draw static images (frames, stickers)
      ctx.save()
      // Apply rotation if specified
      if (imageParams.rotation) {
        const centerX = imageParams.left + imageParams.width / 2
        const centerY = imageParams.top + imageParams.height / 2
        ctx.translate(centerX, centerY)
        ctx.rotate(imageParams.rotation)
        ctx.translate(-centerX, -centerY)
      }
      ctx.drawImage(
        loadedAssets[imageParams.src || ''] as CanvasImageSource,
        imageParams.left,
        imageParams.top,
        imageParams.width,
        imageParams.height
      )
      ctx.restore()
    } else if (imageParams.type === 'qr') {
      // Draw QR code as ImageBitmap
      ctx.save()
      // Apply rotation if specified
      if (imageParams.rotation) {
        const centerX = imageParams.left + imageParams.width / 2
        const centerY = imageParams.top + imageParams.height / 2
        ctx.translate(centerX, centerY)
        ctx.rotate(imageParams.rotation)
        ctx.translate(-centerX, -centerY)
      }
      ctx.drawImage(
        qrImage,
        imageParams.left,
        imageParams.top,
        imageParams.width,
        imageParams.height
      )
      ctx.restore()
    } else if (imageParams.type.startsWith('photo-')) {
      const photoIndex = parseInt(imageParams.type.split('-')[1])
      const photoState = photoStates?.[`photo-${photoIndex}`]
      const slotMeta = (imageParams as { slotMeta?: RawSlotMeta }).slotMeta

      if (photoIndex === currentVideoIndex) {
        // Draw current resource (photo or video)
        if (showPhotos && loadedAssets[`photo-${photoIndex}`]) {
          drawCenterCropped(
            ctx,
            loadedAssets[`photo-${photoIndex}`] as HTMLImageElement,
            imageParams.left,
            imageParams.top,
            imageParams.width,
            imageParams.height,
            photoState,
            imageParams.rotation,
            slotMeta
          )
        }
        if (videoElement && videoElement.readyState >= 2) {
          const videoPhotoState = buildVideoPhotoStateForPlaceholder(
            videoElement,
            imageParams.width,
            imageParams.height,
            photoState
          )

          drawCenterCropped(
            ctx,
            videoElement,
            imageParams.left,
            imageParams.top,
            imageParams.width,
            imageParams.height,
            videoPhotoState,
            imageParams.rotation,
            slotMeta
          )
        }
      } else if (photoIndex <= totalResources && loadedAssets[`photo-${photoIndex}`]) {
        // Draw other photos
        drawCenterCropped(
          ctx,
          loadedAssets[`photo-${photoIndex}`] as HTMLImageElement,
          imageParams.left,
          imageParams.top,
          imageParams.width,
          imageParams.height,
          photoState,
          imageParams.rotation,
          slotMeta
        )
      }
    }
  }

  // Wait for next frame to ensure drawing is complete
  await new Promise((resolve) => requestAnimationFrame(resolve))

  // Now step the recorder with the completed frame
  await canvasRecorder.step()
}

// Modify processVideo to accept video element instead of URL
async function processVideo(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement, // Changed parameter
  drawParams: FrameConfig['drawParams'],
  loadedAssets: Record<string, HTMLImageElement | ImageBitmap>,
  qrImage: ImageBitmap,
  currentIndex: number,
  canvasRecorder: Recorder,
  stepInterval: number,
  totalResources: number,
  photoStates?: Record<string, PhotoState>
): Promise<void> {
  return new Promise((resolve) => {
    video.play()

    const stepLoop = setInterval(async () => {
      if (canvasRecorder.status !== RecorderStatus.Recording) {
        clearInterval(stepLoop)
        return
      }

      await renderFrame(
        ctx,
        drawParams,
        loadedAssets,
        qrImage,
        currentIndex,
        true,
        video,
        canvasRecorder,
        totalResources,
        photoStates
      )

      if (video.ended) {
        clearInterval(stepLoop)
        resolve()
      }
    }, stepInterval)
  })
}

/**
 * Validates and resizes dimensions to ensure they're within codec limits
 * Maximum dimensions: 1920px per dimension (maintaining aspect ratio)
 * Maximum coded area for AVC level 4.1: 2097152 pixels (width and height rounded up to nearest 16)
 * Ensures dimensions are even numbers for codec compatibility
 */
const validateDimensions = (
  width: number,
  height: number
): { width: number; height: number; scaleFactor: number } => {
  const MAX_DIMENSION = 1920
  const MAX_CODED_AREA = 2097152 // AVC level 4.1 maximum coded area
  const originalWidth = width
  const originalHeight = height

  let validatedWidth = width
  let validatedHeight = height

  // Helper function to calculate coded area (dimensions rounded up to nearest 16)
  const getCodedArea = (w: number, h: number): number => {
    const codedWidth = Math.ceil(w / 16) * 16
    const codedHeight = Math.ceil(h / 16) * 16
    return codedWidth * codedHeight
  }

  // First, check if either dimension exceeds maximum
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const aspectRatio = width / height

    if (width > height) {
      // Landscape: limit width
      validatedWidth = MAX_DIMENSION
      validatedHeight = Math.round(MAX_DIMENSION / aspectRatio)
    } else {
      // Portrait or square: limit height
      validatedHeight = MAX_DIMENSION
      validatedWidth = Math.round(MAX_DIMENSION * aspectRatio)
    }

    console.log(
      `[Video Generation] Resizing video dimensions from ${originalWidth}x${originalHeight} to ${validatedWidth}x${validatedHeight} to meet per-dimension codec limits`
    )
  }

  // Ensure dimensions are even numbers (required for codec compatibility)
  validatedWidth = Math.floor(validatedWidth / 2) * 2
  validatedHeight = Math.floor(validatedHeight / 2) * 2

  // Check coded area limit (AVC level 4.1 constraint)
  let codedArea = getCodedArea(validatedWidth, validatedHeight)
  if (codedArea > MAX_CODED_AREA) {
    const aspectRatio = validatedWidth / validatedHeight
    // Scale down to fit within coded area limit
    // Use iterative approach to find the right scale
    let scale = Math.sqrt(MAX_CODED_AREA / codedArea) * 0.98 // 2% safety margin
    let previousCodedArea = codedArea

    // Iteratively adjust until coded area is within limit
    for (let i = 0; i < 10; i++) {
      validatedWidth = Math.floor(validatedWidth * scale)
      validatedHeight = Math.floor(validatedHeight * scale)

      // Ensure even numbers
      validatedWidth = Math.floor(validatedWidth / 2) * 2
      validatedHeight = Math.floor(validatedHeight / 2) * 2

      codedArea = getCodedArea(validatedWidth, validatedHeight)

      if (codedArea <= MAX_CODED_AREA) {
        break
      }

      // Adjust scale based on how much we need to reduce
      scale = Math.sqrt(MAX_CODED_AREA / codedArea) * 0.98

      // Prevent infinite loop
      if (codedArea >= previousCodedArea) {
        // If we're not making progress, reduce by fixed amount
        validatedWidth -= 16
        validatedHeight = Math.floor((validatedWidth / aspectRatio) / 2) * 2
        codedArea = getCodedArea(validatedWidth, validatedHeight)
        break
      }

      previousCodedArea = codedArea
    }

    // Final safety check: reduce by macroblock increments if still too large
    while (codedArea > MAX_CODED_AREA) {
      validatedWidth -= 16
      validatedHeight = Math.floor((validatedWidth / aspectRatio) / 2) * 2
      codedArea = getCodedArea(validatedWidth, validatedHeight)
    }

    console.log(
      `[Video Generation] Resizing video dimensions from ${originalWidth}x${originalHeight} to ${validatedWidth}x${validatedHeight} to meet coded area limit (coded area: ${codedArea} <= ${MAX_CODED_AREA})`
    )
  }

  // Calculate scale factor for proper rendering
  const scaleFactor = validatedWidth / originalWidth

  return {
    width: validatedWidth,
    height: validatedHeight,
    scaleFactor
  }
}

/** Pause at t≈0 with a decodable first frame so the first encoded frame is video, not an empty slot. */
async function prepareLiveVideoAtStart(video: HTMLVideoElement): Promise<void> {
  video.pause()
  try {
    if (Math.abs(video.currentTime) > 0.02) {
      await new Promise<void>((resolve) => {
        const done = (): void => {
          video.removeEventListener('seeked', done)
          resolve()
        }
        video.addEventListener('seeked', done, { once: true })
        video.currentTime = 0
      })
    } else {
      video.currentTime = 0
    }
  } catch {
    /* ignore seek errors */
  }
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await new Promise<void>((resolve) => {
      video.addEventListener('loadeddata', () => resolve(), { once: true })
      video.addEventListener('canplay', () => resolve(), { once: true })
    })
  }
}

/**
 * Generate a Live Mode video.
 *
 * Live Mode now renders only the countdown videos (no pre/post photo strip),
 * using the frame layout and photoStates (including mirroring) to position content.
 *
 * @param resources - Array of captured items with their video and photo URLs
 * @param frameConfig - Frame configuration including drawParams layout
 * @param scaleFactor - Scaling factor for the canvas
 * @param id - Session/gallery identifier (used for QR code and filename)
 * @param recordingSpeed - Speed factor for recording
 * @param FPS - Target frames per second
 * @param photoStates - Per-photo transform and mirroring state from PrintPreview
 * @param photoDuration - Kept for backward compatibility but ignored (no pre/post photos)
 */
export async function generateLiveMode(
  resources: { videoUrl: string; photoUrl: string }[],
  frameConfig: FrameConfig,
  scaleFactor: number = 1,
  id: string,
  recordingSpeed: number,
  FPS: number = 30,
  photoStates?: Record<string, PhotoState>,
  // photoDuration is kept for backward compatibility but ignored:
  // Live Mode is now pure video without pre/post photo frames.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _photoDuration: number = 100
): Promise<File> {
  const { drawParams } = frameConfig
  // Calculate initial dimensions based on scaleFactor
  const initialWidth = Math.round(drawParams.canvasWidth * scaleFactor)
  const initialHeight = Math.round(drawParams.canvasHeight * scaleFactor)

  // Validate dimensions (may resize if exceeds codec limits)
  const validatedDimensions = validateDimensions(initialWidth, initialHeight)

  // Create canvas with validated dimensions
  const canvas = document.createElement('canvas')
  canvas.width = validatedDimensions.width
  canvas.height = validatedDimensions.height
  const ctx = canvas.getContext('2d')!

  // Calculate combined scale factor (scaleFactor * validation scale)
  // This ensures proper rendering when dimensions are resized
  const combinedScaleFactor = scaleFactor * validatedDimensions.scaleFactor

  const canvasRecorder = new Recorder(ctx as RenderingContext, {
    name: 'dreambooth-live',
    encoderOptions: {
      codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
      width: validatedDimensions.width,
      height: validatedDimensions.height,
      bitrate: 2000000
    },
    duration: Infinity,
    download: false,
    frameRate: FPS
  })

  // Preload all videos; allow empty/invalid videoUrl (static photo fallback for that slot)
  const loadedVideos: (HTMLVideoElement | null)[] = []
  await Promise.all(
    resources.map(async (resource) => {
      const hasValidVideoUrl =
        resource.videoUrl &&
        resource.videoUrl.trim() !== '' &&
        (resource.videoUrl.startsWith('blob:') || resource.videoUrl.startsWith('http'))

      if (!hasValidVideoUrl) {
        loadedVideos.push(null)
        return
      }

      const video = document.createElement('video')
      video.muted = true
      video.autoplay = false
      video.loop = false
      video.src = resource.videoUrl
      video.preload = 'metadata'

      try {
        await Promise.all([
          new Promise<void>((resolve, reject) => {
            video.onloadedmetadata = (): void => resolve()
            video.onerror = (): void => {
              console.warn(`Error loading video metadata: ${resource.videoUrl}`)
              reject(new Error(`Failed to load video metadata: ${resource.videoUrl}`))
            }
          }),
          new Promise<void>((resolve, reject) => {
            video.onloadeddata = (): void => resolve()
            video.onerror = (): void => {
              console.warn(`Error loading video: ${resource.videoUrl}`)
              reject(new Error(`Failed to load video: ${resource.videoUrl}`))
            }
          })
        ])
        loadedVideos.push(video)
      } catch {
        loadedVideos.push(null)
      }
    })
  )

  const stepInterval = 1000 / (FPS / recordingSpeed)

  // Apply combined scale transformation to match original frame dimensions
  ctx.scale(combinedScaleFactor, combinedScaleFactor)

  await Promise.all(
    loadedVideos.map((v) => (v ? prepareLiveVideoAtStart(v) : Promise.resolve()))
  )

  // Preload all assets
  const loadedAssets: Record<string, HTMLImageElement> = {}

  // Load QR code as ImageBitmap
  const qrImage = await imageUtils.generateQRCode(id)

  // Preload all photos
  for (let i = 0; i < resources.length; i++) {
    const photoUrl = resources[i].photoUrl
    if (photoUrl) {
      const img = new Image()
      img.src = photoUrl
      await new Promise<void>((resolve) => {
        img.onload = (): void => resolve()
        img.onerror = (): void => {
          console.error(`Failed to load photo: ${photoUrl}`)
          resolve()
        }
      })
      loadedAssets[`photo-${i + 1}`] = img
    }
  }

  // Preload other static images
  await Promise.all(
    drawParams.images
      .filter((img) => img.type === 'image')
      .map(async (imageParams): Promise<void> => {
        const imgSrc = imageParams.src || ''
        if (!imgSrc) return

        try {
          const response = await fetch(imgSrc, { method: 'GET', mode: 'cors' })
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
          const blob = await response.blob()
          const img = new Image()
          img.src = URL.createObjectURL(blob)
          await new Promise<void>((resolve) => {
            img.onload = (): void => resolve()
            img.onerror = (): void => {
              console.error(`Failed to load image: ${imgSrc}`)
              resolve()
            }
          })
          loadedAssets[imgSrc] = img
        } catch (error) {
          console.error('Error loading image:', error)
        }
      })
  )

  canvasRecorder.start()

  const videoEndedPromises = loadedVideos.map((video) => {
    if (!video) return Promise.resolve()
    return new Promise<void>((resolve) => {
      const stepLoop = setInterval(() => {
        if (video.ended || canvasRecorder.status !== RecorderStatus.Recording) {
          clearInterval(stepLoop)
          resolve()
        }
      }, stepInterval)
    })
  })

  await Promise.all(
    loadedVideos.map((v) =>
      v
        ? v.play().catch((error) => {
            console.error('Error playing video:', error)
          })
        : Promise.resolve()
    )
  )

  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

  // Render frames while videos are playing (null slots count as "ended")
  while (!loadedVideos.every((v) => v === null || v.ended)) {
    // Draw background
    ctx.fillStyle = drawParams.backgroundColor || '#FFFFFF'
    ctx.fillRect(
      0,
      0,
      ctx.canvas.width / combinedScaleFactor,
      ctx.canvas.height / combinedScaleFactor
    )

    // Draw all elements in their original order
    for (const imageParams of drawParams.images) {
      if (imageParams.type.startsWith('photo-')) {
        const photoIndex = parseInt(imageParams.type.split('-')[1])
        const photoState = photoStates?.[`photo-${photoIndex}`]
        const video = loadedVideos[photoIndex - 1]
        const slotMeta = (imageParams as { slotMeta?: RawSlotMeta }).slotMeta

        if (video) {
          const videoPhotoState = buildVideoPhotoStateForPlaceholder(
            video,
            imageParams.width,
            imageParams.height,
            photoState
          )

          drawCenterCropped(
            ctx,
            video,
            imageParams.left,
            imageParams.top,
            imageParams.width,
            imageParams.height,
            videoPhotoState,
            imageParams.rotation,
            slotMeta
          )
        } else if (loadedAssets[`photo-${photoIndex}`]) {
          // Static photo fallback when video is missing
          drawCenterCropped(
            ctx,
            loadedAssets[`photo-${photoIndex}`],
            imageParams.left,
            imageParams.top,
            imageParams.width,
            imageParams.height,
            photoState ?? undefined,
            imageParams.rotation,
            slotMeta
          )
        }
      } else if (imageParams.type === 'image' && loadedAssets[imageParams.src || '']) {
        drawStaticLayerInFrame(
          ctx,
          loadedAssets[imageParams.src || ''] as CanvasImageSource,
          imageParams.left,
          imageParams.top,
          imageParams.width,
          imageParams.height,
          imageParams.rotation
        )
      } else if (imageParams.type === 'qr') {
        drawStaticLayerInFrame(
          ctx,
          qrImage,
          imageParams.left,
          imageParams.top,
          imageParams.width,
          imageParams.height,
          imageParams.rotation
        )
      }
    }

    await canvasRecorder.step()
    await new Promise((resolve) => setTimeout(resolve, stepInterval))
  }

  // Render final frame with all videos showing their last frames.
  // IMPORTANT: use combinedScaleFactor (not scaleFactor) so the clear rect covers exactly the
  // same unscaled region as the per-frame clear at the top of the render loop above.
  // Using the wrong factor leaves a mis-sized background band (often visible as a solid color
  // strip at the edge of the video or, on transparent canvases, as green in H.264 output).
  ctx.fillStyle = drawParams.backgroundColor || '#FFFFFF'
  ctx.fillRect(0, 0, ctx.canvas.width / combinedScaleFactor, ctx.canvas.height / combinedScaleFactor)

  for (const imageParams of drawParams.images) {
    if (imageParams.type.startsWith('photo-')) {
      const photoIndex = parseInt(imageParams.type.split('-')[1])
      const photoState = photoStates?.[`photo-${photoIndex}`]
      const video = loadedVideos[photoIndex - 1]
      const slotMeta = (imageParams as { slotMeta?: RawSlotMeta }).slotMeta

      if (video) {
        const videoPhotoState = buildVideoPhotoStateForPlaceholder(
          video,
          imageParams.width,
          imageParams.height,
          photoState
        )

        drawCenterCropped(
          ctx,
          video,
          imageParams.left,
          imageParams.top,
          imageParams.width,
          imageParams.height,
          videoPhotoState,
          imageParams.rotation,
          slotMeta
        )
      } else if (loadedAssets[`photo-${photoIndex}`]) {
        drawCenterCropped(
          ctx,
          loadedAssets[`photo-${photoIndex}`],
          imageParams.left,
          imageParams.top,
          imageParams.width,
          imageParams.height,
          photoState ?? undefined,
          imageParams.rotation,
          slotMeta
        )
      }
    } else if (imageParams.type === 'image' && loadedAssets[imageParams.src || '']) {
      drawStaticLayerInFrame(
        ctx,
        loadedAssets[imageParams.src || ''] as CanvasImageSource,
        imageParams.left,
        imageParams.top,
        imageParams.width,
        imageParams.height,
        imageParams.rotation
      )
    } else if (imageParams.type === 'qr') {
      drawStaticLayerInFrame(
        ctx,
        qrImage,
        imageParams.left,
        imageParams.top,
        imageParams.width,
        imageParams.height,
        imageParams.rotation
      )
    }
  }

  await canvasRecorder.step()

  await Promise.all(videoEndedPromises)

  const recordedChunks = await canvasRecorder.stop()
  const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
  return new File([mp4Blob], `dreambooth-${id}-live.mp4`, { type: 'video/mp4' })
}
